// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import { useDevice, useModels } from "../../../Api/newSockets.api";
import dataApi from "../../../Api/data.api";
import CapturePreviewModal from "../../../components/Modals/CapturePreviewModal";
import ChessboardCalibrate from "../../../components/Annotation/ChessboardCalibrate";
import "../../../styles/mes.scss";

interface CameraDeviceMetricsProps {
  device: any;
  deviceId: number;
  isOnline: boolean;
}

const CameraDeviceMetrics: React.FC<CameraDeviceMetricsProps> = ({
  device,
  deviceId,
  isOnline,
}) => {
  const [copiedField, setCopiedField] = useState<'ip' | 'token' | 'stream' | null>(null);

  // Stream state
  const [streamImg, setStreamImg] = useState('');

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [capturedProcessedImageData, setCapturedProcessedImageData] = useState('');
  const [capturedOriginalImageData, setCapturedOriginalImageData] = useState('');
  const [capturedMetrics, setCapturedMetrics] = useState<any>({});
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Chessboard calibration state
  const [chessboardCalibrationActive, setChessboardCalibrationActive] = useState(false);

  // Model selection state
  const [selectedModelId, setSelectedModelId] = useState('');

  // Settings modal state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [newDatasetName, setNewDatasetName] = useState('');
  const [creatingDataset, setCreatingDataset] = useState(false);

  // Hooks
  const { rotateStream, cameraCapture, confirmCapture, setDeviceSettings, cameraCalibrate } = useDevice();
  const { models, searchModels, connected: modelsConnected } = useModels();

  // Datasets are fetched over REST (request/response) rather than via the socket
  // 'datasets' event. The socket path is race-prone on a hard reload: the
  // getDatasets emit and the event listener attach in separate effects, so a fast
  // response can arrive before the listener exists and is silently dropped, leaving
  // this dropdown empty. A REST GET on mount has no such race and is retryable.
  const [datasets, setDatasets] = useState<any[]>([]);

  const fetchDatasets = async () => {
    try {
      const res = await dataApi.getDatasets(0, 200, { type: 'Metrics' });
      setDatasets(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch datasets:', err);
    }
  };

  const copyToClipboard = (text: string, field: 'ip' | 'token' | 'stream') => {
    if (!text || text === '—') return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Camera theme color (purple)
  const themeColor = '#9c27b0';

  // Get camera settings
  const settings = device?.settings || {};
  const streamEndpoint = settings.cameraStreamEndpoint || '';
  const calibration = settings.calibration || settings;
  const isCalibrated = calibration?.points && calibration.points.length > 0;

  // Initialize stream URL, model, and dataset
  useEffect(() => {
    if (device && streamEndpoint) {
      setStreamImg(`${streamEndpoint}?cb=${Math.random()}`);
      if (device.settings?.modelId != null) {
        setSelectedModelId(String(device.settings.modelId));
      }
      if (device.settings?.defaultDataset != null) {
        setSelectedDatasetId(String(device.settings.defaultDataset));
      }
    }
  }, [device, streamEndpoint]);

  // Build dataset options
  const datasetOptions = useMemo(() => {
    const arr = Array.isArray(datasets) ? datasets : [];
    return arr
      .filter((d: any) => d && d.id != null)
      .map((d: any) => ({ id: String(d.id), name: d.name ?? String(d.id) }));
  }, [datasets]);

  // Fetch models when socket is connected
  useEffect(() => {
    if (modelsConnected) {
      searchModels('').catch(() => {});
    }
  }, [modelsConnected]);

  // Fetch datasets once on mount via REST (no socket-event timing race)
  useEffect(() => {
    fetchDatasets();
  }, []);

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModelId(e.target.value);
    setDeviceSettings(deviceId, { modelId: e.target.value });
  };

  const handleDatasetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDatasetId(e.target.value);
    setDeviceSettings(deviceId, { defaultDataset: e.target.value });
  };

  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) return;
    setCreatingDataset(true);
    try {
      const created: any = await dataApi.createDataset(
        newDatasetName.trim(),
        'Metrics',
        `Metrics dataset for ${device?.name || 'device'}`
      );
      if (created?.id) {
        setSelectedDatasetId(String(created.id));
        setDeviceSettings(deviceId, { defaultDataset: String(created.id) });
      }
      await fetchDatasets();
      setNewDatasetName('');
    } catch (err) {
      console.error("Failed to create dataset:", err);
    } finally {
      setCreatingDataset(false);
    }
  };

  const rotateImage = (rotation: string) => {
    rotateStream(String(deviceId), rotation);
  };

  const handleCapture = async () => {
    try {
      const response: any = await cameraCapture(
        String(deviceId),
        settings?.defaultDataset ?? null
      );
      const processedData = response?.processedImage ?? response?.image;
      if (response && processedData) {
        setCapturedProcessedImageData(processedData);
        setCapturedOriginalImageData(response?.originalImage || '');
        setCapturedMetrics(response.metrics ?? {});
        setPreviewModalOpen(true);
      } else {
        console.error('Invalid capture response:', response);
        alert('Failed to capture image. Please try again.');
      }
    } catch (error) {
      console.error('Capture error:', error);
      alert('Failed to capture image. Please try again.');
    }
  };

  const runChessboardCalibration = () => {
    cameraCalibrate(String(deviceId), 0, 0, false);
    setChessboardCalibrationActive(true);
  };

  // Button style helper
  const buttonStyle = (primary = false) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: primary ? `1px solid ${themeColor}` : '1px solid #ffffff30',
    background: primary ? `${themeColor}30` : 'transparent',
    color: primary ? '#ce93d8' : '#fff',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: isOnline ? 'pointer' : 'not-allowed',
    opacity: isOnline ? 1 : 0.5,
    transition: 'all 0.2s',
  });

  return (
    <>
      <div style={{ marginTop: '1rem' }}>
        {/* Header with device info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          borderRadius: '12px',
          border: `1px solid ${themeColor}30`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontSize: '1.5rem' }}>
              📷
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#fff',
              }}>
                {device.name}
              </div>
              <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: 'rgba(156, 39, 176, 0.2)',
                border: '1px solid #9c27b0',
                color: '#ce93d8',
              }}>
                {device.deviceTypes?.name || 'Camera'}
              </span>
            </div>
          </div>
          {/* Settings Button */}
          <button
            onClick={() => setSettingsModalOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '1px solid #ffffff30',
              background: 'transparent',
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            title="Device settings"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span>Settings</span>
          </button>
        </div>

        {/* Device Info Cards - Row 1 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.75rem',
          marginBottom: '0.75rem',
        }}>
          {/* Status */}
          <div style={{
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            border: `1px solid ${isOnline ? '#00ff8830' : '#ff336630'}`,
            borderRadius: '10px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <div style={{
              fontSize: '0.65rem',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.25rem',
            }}>
              Status
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              flex: 1,
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: isOnline ? '#00ff88' : '#ff3366',
                boxShadow: isOnline ? '0 0 6px #00ff88' : '0 0 6px #ff3366',
              }} />
              <span style={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: isOnline ? '#00ff88' : '#ff3366',
              }}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>

          {/* IP Address */}
          <div style={{
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            border: `1px solid ${themeColor}20`,
            borderRadius: '10px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <div style={{
              fontSize: '0.65rem',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.25rem',
            }}>
              IP Address
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flex: 1,
              minWidth: 0,
            }}>
              <div style={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#fff',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}>
                {device?.settings?.public_ip || '—'}
              </div>
              {device?.settings?.public_ip && (
                <button
                  onClick={() => copyToClipboard(device?.settings?.public_ip, 'ip')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    color: copiedField === 'ip' ? '#00ff88' : '#6c757d',
                    transition: 'color 0.2s',
                  }}
                  title="Copy to clipboard"
                >
                  {copiedField === 'ip' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Token */}
          <div style={{
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            border: `1px solid ${themeColor}20`,
            borderRadius: '10px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <div style={{
              fontSize: '0.65rem',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.25rem',
            }}>
              Device Token
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flex: 1,
              minWidth: 0,
            }}>
              <div style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#fff',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}>
                {device?.token || '—'}
              </div>
              {device?.token && (
                <button
                  onClick={() => copyToClipboard(device?.token, 'token')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    color: copiedField === 'token' ? '#00ff88' : '#6c757d',
                    transition: 'color 0.2s',
                  }}
                  title="Copy to clipboard"
                >
                  {copiedField === 'token' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Camera-specific Info Cards - Row 2 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}>
          {/* Stream Endpoint */}
          <div style={{
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            border: `1px solid ${themeColor}20`,
            borderRadius: '10px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <div style={{
              fontSize: '0.65rem',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.25rem',
            }}>
              Stream Endpoint
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flex: 1,
              minWidth: 0,
            }}>
              <div style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#fff',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}>
                {streamEndpoint || '—'}
              </div>
              {streamEndpoint && (
                <button
                  onClick={() => copyToClipboard(streamEndpoint, 'stream')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    color: copiedField === 'stream' ? '#00ff88' : '#6c757d',
                    transition: 'color 0.2s',
                  }}
                  title="Copy to clipboard"
                >
                  {copiedField === 'stream' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Calibration Status */}
          <div style={{
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            border: `1px solid ${isCalibrated ? '#00ff8830' : '#ff990030'}`,
            borderRadius: '10px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <div style={{
              fontSize: '0.65rem',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.25rem',
            }}>
              Calibration
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              flex: 1,
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: isCalibrated ? '#00ff88' : '#ff9900',
                boxShadow: isCalibrated ? '0 0 6px #00ff88' : '0 0 6px #ff9900',
              }} />
              <span style={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: isCalibrated ? '#00ff88' : '#ff9900',
              }}>
                {isCalibrated ? 'Calibrated' : 'Not Calibrated'}
              </span>
            </div>
            {isCalibrated && (
              <div style={{
                fontSize: '0.7rem',
                color: '#6c757d',
                textAlign: 'center',
                marginTop: '0.25rem',
              }}>
                {calibration.rows} x {calibration.cols} | {calibration.square_mm}mm
              </div>
            )}
          </div>

          {/* Model Selection */}
          <div style={{
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            border: `1px solid ${themeColor}20`,
            borderRadius: '10px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <div style={{
              fontSize: '0.65rem',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.25rem',
            }}>
              Model
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
            }}>
              <select
                value={selectedModelId}
                onChange={handleModelChange}
                style={{
                  width: '100%',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid #ffffff20',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236c757d' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  paddingRight: '2rem',
                }}
              >
                <option value="" style={{ background: '#1a1f22', color: '#6c757d' }}>
                  Select model
                </option>
                {models?.map((m: any) => (
                  <option
                    key={m.id}
                    value={String(m.id)}
                    style={{ background: '#1a1f22', color: '#fff' }}
                  >
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Camera Controls */}
        {isOnline && streamEndpoint && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem',
            padding: '1rem',
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            borderRadius: '12px',
            border: `1px solid ${themeColor}20`,
            flexWrap: 'wrap',
          }}>
            {/* Rotate Controls - Disabled */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>Rotate:</span>
              <button
                onClick={() => rotateImage("counterclockwise")}
                disabled={true}
                style={{ ...buttonStyle(), opacity: 0.4, cursor: 'not-allowed' }}
                title="Rotate counterclockwise (disabled)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              <button
                onClick={() => rotateImage("clockwise")}
                disabled={true}
                style={{ ...buttonStyle(), opacity: 0.4, cursor: 'not-allowed' }}
                title="Rotate clockwise (disabled)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              </button>
            </div>

            {/* Capture Button */}
            <button
              onClick={handleCapture}
              disabled={!isOnline}
              style={buttonStyle(true)}
              title="Capture image"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <span>Capture</span>
            </button>

            {/* Calibrate Button */}
            <button
              onClick={runChessboardCalibration}
              disabled={!isOnline}
              style={buttonStyle()}
              title="Run chessboard calibration"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              <span>Calibrate</span>
            </button>
          </div>
        )}

        {/* Stream Preview */}
        {streamEndpoint && (
          <div style={{
            background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
            border: `1px solid ${themeColor}30`,
            borderRadius: '12px',
            padding: '1rem',
          }}>
            <div style={{
              fontSize: '0.75rem',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.75rem',
            }}>
              Live Stream
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: '#000',
              borderRadius: '8px',
              overflow: 'hidden',
              minHeight: '200px',
            }}>
              {isOnline ? (
                <img
                  src={streamImg || `${streamEndpoint}?cb=${Date.now()}`}
                  alt="Camera stream"
                  style={{
                    width: '100%',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://upload.wikimedia.org/wikipedia/commons/b/b1/Loading_icon.gif?20151024034921';
                  }}
                />
              ) : (
                <div style={{
                  color: '#6c757d',
                  fontSize: '0.9rem',
                  padding: '2rem',
                  textAlign: 'center',
                }}>
                  Camera is offline
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Chessboard Calibration Modal */}
      {chessboardCalibrationActive && (
        <ChessboardCalibrate
          source={streamEndpoint}
          deviceId={device.id}
          setCalibrationActive={setChessboardCalibrationActive}
        />
      )}

      {/* Capture Preview Modal */}
      {previewModalOpen && (
        <CapturePreviewModal
          visible={previewModalOpen}
          imageData={capturedProcessedImageData}
          metrics={capturedMetrics}
          loading={confirmLoading}
          onConfirm={async () => {
            if (confirmLoading) return;

            setConfirmLoading(true);

            setTimeout(() => {
              setPreviewModalOpen(false);
              setCapturedProcessedImageData('');
              setCapturedOriginalImageData('');
              setCapturedMetrics({});
              setConfirmLoading(false);
            }, 100);

            try {
              await confirmCapture(
                capturedProcessedImageData,
                capturedOriginalImageData,
                capturedMetrics,
                String(deviceId),
                settings?.defaultDataset ?? null,
                'stream-capture'
              );
              console.log('Image saved successfully');
            } catch (error) {
              console.error('Failed to save image:', error);
            }
          }}
          onCancel={() => {
            setPreviewModalOpen(false);
            setCapturedProcessedImageData('');
            setCapturedOriginalImageData('');
            setCapturedMetrics({});
          }}
        />
      )}

      {/* Settings Modal - MES Style */}
      {settingsModalOpen && (
        <div className="mes-modal-overlay" onClick={() => setSettingsModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">⚙️</div>
                <div className="modal-title-text">
                  <h3>CAMERA SETTINGS</h3>
                  <p className="modal-subtitle">Configure {device.name}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setSettingsModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* Model Selection */}
              <div className="form-group">
                <label>Model</label>
                <select
                  value={selectedModelId}
                  onChange={handleModelChange}
                >
                  <option value="">Select model</option>
                  {models?.map((m: any) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Default Dataset Selection */}
              <div className="form-group">
                <label>Default Dataset</label>
                <select
                  value={selectedDatasetId}
                  onChange={handleDatasetChange}
                >
                  <option value="">Select dataset</option>
                  {datasetOptions.map((d: any) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Create New Dataset */}
              <div className="form-group">
                <label>Create New Dataset</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="Dataset name"
                    value={newDatasetName}
                    onChange={(e) => setNewDatasetName(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn-submit"
                    onClick={handleCreateDataset}
                    disabled={!newDatasetName.trim() || creatingDataset}
                    style={{ padding: '0.75rem 1rem' }}
                  >
                    {creatingDataset ? 'Creating...' : '+ Create'}
                  </button>
                </div>
              </div>

              {/* Device Info Section */}
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                border: '1px solid rgba(156, 39, 176, 0.2)',
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.75rem',
                  color: themeColor,
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Device Information
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: '0.5rem 1rem',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace',
                }}>
                  <span style={{ color: '#6c757d' }}>Name:</span>
                  <span style={{ color: '#fff' }}>{device.name}</span>

                  <span style={{ color: '#6c757d' }}>ID:</span>
                  <span style={{ color: '#fff' }}>{device.id}</span>

                  <span style={{ color: '#6c757d' }}>Type:</span>
                  <span style={{ color: '#fff' }}>{device.deviceTypes?.name || 'Camera'}</span>

                  {device.connectedTo && (
                    <>
                      <span style={{ color: '#6c757d' }}>Connected to:</span>
                      <span style={{ color: '#fff' }}>{device.connectedTo}</span>
                    </>
                  )}

                  {streamEndpoint && (
                    <>
                      <span style={{ color: '#6c757d' }}>Stream URL:</span>
                      <span style={{ color: '#fff', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                        {streamEndpoint}
                      </span>
                    </>
                  )}

                  {isCalibrated && (
                    <>
                      <span style={{ color: '#6c757d' }}>Calibration:</span>
                      <span style={{ color: '#fff' }}>
                        {calibration.rows} x {calibration.cols} | {calibration.square_mm}mm
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setSettingsModalOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CameraDeviceMetrics;
